import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';

interface ComplianceRecord {
  id: string;
  productionDate: string;
  releaseDate?: string;
  contentDescription?: string;
  complianceStatus: string;
  model: {
    id: string;
    legalName: string;
    stageNames: string[];
  };
  createdAt: string;
}

export default function Compliance() {
  const { data, isLoading } = useQuery({
    queryKey: ['compliance-records'],
    queryFn: () => api.get<{ records: ComplianceRecord[] }>('/compliance/2257'),
  });

  const records = data?.data.records || [];

  const handleExport = async (format: 'json' | 'csv') => {
    const response = await fetch(`/api/compliance/2257/export?format=${format}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `2257-records.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>2257 Compliance Records</h1>
        <div className="header-actions">
          <button
            onClick={() => handleExport('csv')}
            className="btn btn-secondary"
          >
            Export CSV
          </button>
          <button
            onClick={() => handleExport('json')}
            className="btn btn-secondary"
          >
            Export JSON
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">Loading compliance records...</div>
      ) : records.length === 0 ? (
        <div className="empty-state">
          <p>No 2257 compliance records found</p>
          <p className="text-muted">
            Records are automatically created when documents are completed
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Production Date</th>
                <th>Release Date</th>
                <th>Content</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id}>
                  <td>
                    <Link to={`/models/${record.model.id}`} className="link">
                      {record.model.legalName}
                    </Link>
                  </td>
                  <td>{record.productionDate}</td>
                  <td>{record.releaseDate || '-'}</td>
                  <td className="truncate" title={record.contentDescription}>
                    {record.contentDescription || '-'}
                  </td>
                  <td>
                    <span
                      className={`badge ${record.complianceStatus === 'compliant' ? 'badge-success' : 'badge-warning'}`}
                    >
                      {record.complianceStatus}
                    </span>
                  </td>
                  <td>{new Date(record.createdAt).toLocaleDateString()}</td>
                  <td>
                    <Link to={`/compliance/${record.id}`} className="btn btn-small">
                      View Details
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
